import { API_CONFIG } from '@/config/api.config';

// Use the admin gateway which points to port 8000 where search routes are registered
const API_BASE = process.env.NEXT_PUBLIC_API_GATEWAY_ADMIN?.replace('/v1/v1', '') || 'http://localhost:8000';
const SEARCH_LAMBDA_BASE = process.env.NEXT_PUBLIC_SEARCH_API_GATEWAY
const TENANT_ID = API_CONFIG.TENANT_ID;

// Click beacons go here. LOCAL/DEV: the backend fire-and-forget proxy (below).
// PROD: point this at the isolated ingestion pipeline (API Gateway → SQS →
// DynamoDB, design §12.1) via NEXT_PUBLIC_SEARCH_CLICK_URL — no code change,
// clicks never touch the backend or MySQL on the write path.
const CLICK_URL = process.env.NEXT_PUBLIC_SEARCH_CLICK_URL
    || `${API_BASE}/v1/public/search/v2/click`;

export interface Suggestion {
    content_id: string;
    title: string;
    slug: string;
    summary: string;
}

export interface SearchSuggestion {
    suggestions: Suggestion[];
    // v2 envelope extras (proxy passes them through; legacy omits them)
    search_event_id?: string | null;
}

/** Header result + the telemetry handle the click beacon joins back on. */
export interface HeaderSuggestions {
    suggestions: Suggestion[];
    searchEventId: string | null;
}

async function fetchBody(query: string, path: string): Promise<SearchSuggestion | null> {
    if (!query || query.length < 2) return null;
    try {
        const params = new URLSearchParams({ q: query, limit: '10' });
        const res = await fetch(`${API_BASE}${path}?${params}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', 'X-Tenant-Id': TENANT_ID },
        });
        if (!res.ok) {
            console.error('[SearchService] Failed to fetch suggestions:', res.statusText);
            return null;
        }
        return await res.json();
    } catch (error) {
        console.error('[SearchService] Error fetching search suggestions:', error);
        return null;
    }
}

/**
 * LEGACY suggestions endpoint — FROZEN for the inquiry picker
 * (ReportSearchSelect.tsx) and the payment-link tool
 * (PaymentLinkGeneratorContent.tsx). Design D2: these two do NOT move to v2.
 */
export async function fetchSearchSuggestions(query: string): Promise<Suggestion[]> {
    const body = await fetchBody(query, '/v1/public/search/suggestions');
    return body?.suggestions || [];
}

/**
 * Site-header type-ahead — v2 proxy (SEARCH_V2_INTEGRATION.md SS-3). Returns the
 * suggestions PLUS `search_event_id`, the id the click beacon hands back so a
 * click joins to the search that produced it (funnel attribution, design §12.1).
 * Only SearchInput.tsx uses this.
 */
export async function fetchHeaderSuggestions(query: string): Promise<HeaderSuggestions> {
    const body = await fetchBody(query, '/v1/public/search/v2/suggestions');
    return {
        suggestions: body?.suggestions || [],
        searchEventId: body?.search_event_id ?? null,
    };
}

/** Where a click was made and what it was. Every action a visitor can take
 *  on a search result is its own surface, so the console counts them apart:
 *  the header dropdown, and on a results-page card the report link, Request a
 *  Sample, Enquire Before Buying and Add to Cart. */
export type ClickSurface = 'dropdown' | 'results' | 'results-sample' | 'results-enquiry' | 'results-cart';
/** The four actions on a result card, mapped to their click surfaces. */
export type CardAction = 'open' | 'sample' | 'enquiry' | 'cart';
export const CARD_ACTION_SURFACE: Record<CardAction, ClickSurface> = {
    open: 'results', sample: 'results-sample', enquiry: 'results-enquiry', cart: 'results-cart',
};

/**
 * Fire-and-forget click beacon. `sendBeacon` survives the page unload that a
 * navigation triggers and never delays it; a failure is silent by design — a
 * click must never cost the visitor anything (design §12.1). No-op when we have
 * no search_event_id (e.g. legacy/fallback response carried none).
 *
 * `position` is 0-based; on the results page it is GLOBAL across pages
 * ((page-1)*page_size + index), so the funnel can tell "clicked #1" from
 * "scrolled to page 3". `surface` lets the console compare the two funnels
 * (SEARCH_CLICK_FEEDBACK_DESIGN.md §2.2). One contract for every surface.
 */
export function sendSearchClick(
    searchEventId: string | null,
    contentId: string,
    position: number,
    surface: ClickSurface = 'dropdown',
): void {
    if (!searchEventId || typeof navigator === 'undefined' || !navigator.sendBeacon) return;
    try {
        const blob = new Blob(
            [JSON.stringify({ search_event_id: searchEventId, content_id: contentId, position, surface })],
            { type: 'application/json' },
        );
        navigator.sendBeacon(CLICK_URL, blob);
    } catch {
        /* clicks are best-effort — never surface an error to the visitor */
    }
}
